import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFieldInfoFromDataFlowEdgeHandle} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  FlowchartEdge,
  ProcessFlowchartEdgeType,
  TransitionSource,
  TaskSchemaNodeSection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNode,
  FlowchartNode,
  isInteractiveTaskNode,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSourceForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import {getFieldPairKey} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionKeyUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {Transition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionTypes';
import {getExecutionNodeTypeIdentifierForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {ORANGE_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {findAllDownstreamMappableNodeIds, findAllUpstreamMappableNodeIds} from '../TraverseFlowchart';
import CollapsibleTransitionCard from './CollapsibleTransitionCard';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {
  getTransitionSourceLabel,
  StaticTransitionSourceDescription,
  useTransitionCardStyles,
} from './TransitionCardConstants';
import {TransitionMappingsTable} from './TransitionFieldInfoSection';

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

enum TransitionDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

interface TransitionSourceDescriptionProps {
  isRootSourceNode: boolean;
  isTargetOutputNode: boolean;
  transitionSource: TransitionSource;
  availableTransitionSources: Array<TransitionSource>;
  connectedNodeId: string;
  connectedNodeName: string;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource, transition: Transition) => void;
  transition: Transition;
  locked?: boolean;
}

/**
 * Renders the description for a transition source, including a dropdown to select between
 * available sources (e.g. Outputs vs Tasks). For root source nodes, displays a static label
 * instead. Shows a warning when the Tasks source is selected, since it limits field mappings
 * to input fields from the previous step.
 */
function TransitionSourceDescription(props: TransitionSourceDescriptionProps): JSX.Element {
  const {
    isRootSourceNode,
    isTargetOutputNode,
    transitionSource,
    availableTransitionSources,
    connectedNodeId,
    connectedNodeName,
    handleTransitionSourceSelect,
    transition,
    locked,
  } = props;

  if (isRootSourceNode) {
    return (
      <div className={css(styles.cardBody)}>
        <StaticTransitionSourceDescription transitionSource={TransitionSource.INPUTS} />
      </div>
    );
  }

  return (
    <div className={css(styles.cardBody)}>
      <span>Use&nbsp;</span>
      <div className={css(styles.fieldSourceDropdownWrapper)}>
        <SelectDropdown
          dataTestAttrs={{
            component: 'TransitionSourceDropdown',
            element: 'root',
            key: connectedNodeName,
          }}
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE} value={transitionSource}>
              {availableTransitionSources.map((source) => (
                <Option
                  key={source}
                  value={source}
                  onSelect={(value: TransitionSource) => {
                    handleTransitionSourceSelect(connectedNodeId, value, transition);
                  }}
                  isDisabled={locked}
                >
                  {getTransitionSourceLabel(source)}
                </Option>
              ))}
            </Menu>
          }
          displayName={getTransitionSourceLabel(transitionSource)}
        />
      </div>
      {isTargetOutputNode ? (
        <span>&nbsp;to populate output fields on this flowchart</span>
      ) : (
        <span>&nbsp;to create tasks &amp; populate fields</span>
      )}
      {transitionSource === TransitionSource.INPUTS && (
        <div className={css(styles.warningMessage)}>
          <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
          <span className={css(styles.secondaryText)}>
            Limits field mappings to input fields from the previous step
          </span>
        </div>
      )}
    </div>
  );
}

interface TransitionCardFieldMappingsProps {
  selectedNode: TaskSchemaNode;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
}

/**
 * Renders the field mapping tables for a transition's data flow edges within a collapsible
 * card. Shows an empty state message when no edges exist.
 */
function TransitionCardFieldMappings(props: TransitionCardFieldMappingsProps): JSX.Element {
  const {classes: cardClasses} = useTransitionCardStyles();
  const {selectedNode, edges, locked} = props;
  const context = NodeDetailsSidepanelContext.useContext();

  if (edges.length === 0) {
    return <div className={css(styles.emptyMappingsText)}>No field mappings configured yet.</div>;
  }

  return (
    <TransitionMappingsTable
      selectedFieldId=""
      node={selectedNode}
      flowchart={context.interactiveFlowchart}
      flowchartTaskSchema={context.flowchartTaskSchema}
      allSchemasById={context.allSchemasById}
      onChangeCustomMappingData={context.onChangeCustomMappingData}
      onChangeSelectedFieldId={noop}
      locked={locked}
      validationErrors={[]}
      edges={edges}
      showBorder={false}
      className={cardClasses.flushTable}
    />
  );
}

interface TransitionTaskSchemaContentProps {
  direction: TransitionDirection;
  connectedNodes: Array<TaskSchemaNode>;
  transitionSourceNode: TaskSchemaNode;
  sidepanelSelectedNode: TaskSchemaNode;
  selectedNodeIsOutputNode: boolean;
  transitionSource: TransitionSource;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource, transition: Transition) => void;
  transition: Transition;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
  isPartOfMergingTransition: boolean;
}

const DIRECTION_CONFIG: Record<
  TransitionDirection,
  {title: string; emptyMessage: string; preposition: string}
> = {
  [TransitionDirection.INBOUND]: {
    title: 'Inbound transitions',
    emptyMessage: 'Draw mappings to this task schema to configure them here.',
    preposition: 'into',
  },
  [TransitionDirection.OUTBOUND]: {
    title: 'Outbound transitions',
    emptyMessage: 'Draw mappings from this task schema to configure them here.',
    preposition: 'from',
  },
};

/**
 * Derives display properties for a TransitionSourceDescription based on the node pair's
 * direction and types.
 */
function getTransitionSourceDescriptionProps(
  direction: TransitionDirection,
  connectedNode: TaskSchemaNode,
  transitionSourceNode: TaskSchemaNode,
  selectedNodeIsOutputNode: boolean
): Pick<
  TransitionSourceDescriptionProps,
  'isRootSourceNode' | 'isTargetOutputNode' | 'availableTransitionSources'
> {
  const isRootSourceNode =
    direction === TransitionDirection.INBOUND &&
    connectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;

  const isTargetOutputNode =
    direction === TransitionDirection.INBOUND
      ? selectedNodeIsOutputNode
      : connectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

  const hasOutputSection = transitionSourceNode.data.sections.some(
    (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
  );
  const availableTransitionSources = hasOutputSection
    ? Object.values(TransitionSource)
    : [TransitionSource.INPUTS];

  return {isRootSourceNode, isTargetOutputNode, availableTransitionSources};
}

/**
 * Per-task schema content for a transition, including a node chip to show which nodes are from this
 * task schema, a transition source description allowing us to choose a transition source, and field
 * mapping tables for configuration of the transition's data flow edges.
 */
function TransitionTaskSchemaContent(props: TransitionTaskSchemaContentProps): JSX.Element {
  const {classes: cardClasses, cx: cardCx} = useTransitionCardStyles();
  const {
    direction,
    connectedNodes,
    transitionSourceNode,
    sidepanelSelectedNode,
    selectedNodeIsOutputNode,
    transitionSource,
    handleTransitionSourceSelect,
    transition,
    edges,
    locked,
    isPartOfMergingTransition,
  } = props;

  const {isRootSourceNode, isTargetOutputNode, availableTransitionSources} =
    getTransitionSourceDescriptionProps(
      direction,
      connectedNodes[0],
      transitionSourceNode,
      selectedNodeIsOutputNode
    );

  return (
    // Merging transitions have a couple of extra components (each node gets its own section inside the card body)
    <div className={isPartOfMergingTransition ? css(styles.mergingNodeBlock) : undefined}>
      {isPartOfMergingTransition && (
        <div className={css(styles.mergingNodeBlockHeader)}>
          {connectedNodes.map((connectedNode) => (
            <Chip
              key={connectedNode.id}
              className={cardCx(cardClasses.chipNormalWeight, cardClasses.displayOnlyChip)}
              text={connectedNode.data.nodeName}
              icon={connectedNode.data.nodeIcon}
              size={ChipSize.MINI}
            />
          ))}
        </div>
      )}
      {isPartOfMergingTransition && (
        <TransitionSourceDescription
          isRootSourceNode={isRootSourceNode}
          isTargetOutputNode={isTargetOutputNode}
          transitionSource={transitionSource}
          availableTransitionSources={availableTransitionSources}
          connectedNodeId={connectedNodes[0].id}
          connectedNodeName={connectedNodes[0].data.nodeName}
          handleTransitionSourceSelect={handleTransitionSourceSelect}
          transition={transition}
          locked={locked}
        />
      )}
      <TransitionCardFieldMappings selectedNode={sidepanelSelectedNode} edges={edges} locked={locked} />
    </div>
  );
}

interface TransitionGroupContentProps {
  direction: TransitionDirection;
  selectedNode: TaskSchemaNode;
  selectedNodeIsOutputNode: boolean;
  transition: Transition;
  connectedNodes: Array<TaskSchemaNode>;
  handleTransitionSourceSelect: (
    nodeIdToUpdate: string,
    value: TransitionSource,
    transition: Transition
  ) => void;
  locked?: boolean;
}

function TransitionGroupContent(props: TransitionGroupContentProps): JSX.Element {
  const {classes: cardClasses, cx: cardCx} = useTransitionCardStyles();
  const {
    direction,
    transition,
    connectedNodes,
    selectedNode,
    selectedNodeIsOutputNode,
    handleTransitionSourceSelect,
    locked,
  } = props;
  const {interactiveFlowchart} = NodeDetailsSidepanelContext.useContext();

  const isMerging = direction === TransitionDirection.INBOUND && transition.sourcePTDVIds.length > 1;

  function getDataFlowEdgesForConnectedNode(connectedNodeId: string): Array<FlowchartEdge> {
    const [sourceId, targetId] =
      direction === TransitionDirection.INBOUND
        ? [connectedNodeId, selectedNode.id]
        : [selectedNode.id, connectedNodeId];
    const sourceNode = findInteractiveTaskNodeById(interactiveFlowchart.nodes, sourceId);
    assert(sourceNode !== undefined, 'Failed to find source node for transition edge');
    const targetNode = findInteractiveTaskNodeById(interactiveFlowchart.nodes, targetId);
    assert(targetNode !== undefined, 'Failed to find target node for transition edge');
    return interactiveFlowchart.edges.filter(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.source === sourceId &&
        edge.target === targetId &&
        transition.customMappingDataByFieldPair[
          getFieldPairKey({
            sourcePTDVId: getTaskSchemaNodePTDVId(sourceNode),
            sourceSectionType: getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).sectionType,
            sourceFieldClientId: getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId,
            targetPTDVId: getTaskSchemaNodePTDVId(targetNode),
            targetSectionType: getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).sectionType,
            targetFieldClientId: getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).fieldClientId,
          })
        ] !== undefined
    );
  }

  function getTransitionSourceNode(connectedNode: TaskSchemaNode): TaskSchemaNode {
    return direction === TransitionDirection.INBOUND ? connectedNode : selectedNode;
  }
  const connectedNodesByPTDVId: Record<string, Array<TaskSchemaNode>> = connectedNodes.reduce(
    (acc, node) => {
      const ptdvId = getTaskSchemaNodePTDVId(node);
      acc[ptdvId] = acc[ptdvId] ?? [];
      acc[ptdvId].push(node);
      return acc;
    },
    {} as Record<string, Array<TaskSchemaNode>>
  );

  const singleNodeSourceDescription = !isMerging
    ? (() => {
        const node = connectedNodes[0];
        const {isRootSourceNode, isTargetOutputNode, availableTransitionSources} =
          getTransitionSourceDescriptionProps(
            direction,
            node,
            getTransitionSourceNode(node),
            selectedNodeIsOutputNode
          );
        return (
          <TransitionSourceDescription
            isRootSourceNode={isRootSourceNode}
            isTargetOutputNode={isTargetOutputNode}
            transitionSource={getTransitionSourceForNode(transition, getTransitionSourceNode(node))}
            availableTransitionSources={availableTransitionSources}
            connectedNodeId={node.id}
            connectedNodeName={node.data.nodeName}
            handleTransitionSourceSelect={handleTransitionSourceSelect}
            transition={transition}
            locked={locked}
          />
        );
      })()
    : undefined;

  return (
    <CollapsibleTransitionCard
      header={direction === TransitionDirection.INBOUND ? 'From' : 'To'}
      headerAdornment={
        <>
          {connectedNodes.map((node) => (
            <Chip
              className={cardCx(cardClasses.chipNormalWeight, cardClasses.displayOnlyChip)}
              key={node.id}
              text={node.data.nodeName}
              icon={node.data.nodeIcon}
              size={ChipSize.MINI}
            />
          ))}
        </>
      }
      alwaysVisibleContent={singleNodeSourceDescription}
      fullWidthChildren={true}
    >
      {Object.values(connectedNodesByPTDVId).map((connectedNodesForPTDVId) => {
        const edgesForPair = getDataFlowEdgesForConnectedNode(connectedNodesForPTDVId[0].id);

        return (
          <TransitionTaskSchemaContent
            key={connectedNodesForPTDVId[0].id}
            direction={direction}
            connectedNodes={connectedNodesForPTDVId}
            transitionSourceNode={getTransitionSourceNode(connectedNodesForPTDVId[0])}
            sidepanelSelectedNode={selectedNode}
            selectedNodeIsOutputNode={selectedNodeIsOutputNode}
            transitionSource={getTransitionSourceForNode(
              transition,
              getTransitionSourceNode(connectedNodesForPTDVId[0])
            )}
            handleTransitionSourceSelect={handleTransitionSourceSelect}
            transition={transition}
            edges={edgesForPair}
            locked={locked}
            isPartOfMergingTransition={isMerging}
          />
        );
      })}
    </CollapsibleTransitionCard>
  );
}

interface TransitionSettingsContentProps {
  direction: TransitionDirection;
  selectedNode: TaskSchemaNode;
  selectedNodeIsOutputNode: boolean;
  transitionGroups: Array<{transition: Transition; nodes: Array<TaskSchemaNode>}>;
  handleTransitionSourceSelect: (
    nodeIdToUpdate: string,
    value: TransitionSource,
    transition: Transition
  ) => void;
  locked?: boolean;
}

/**
 * Renders the settings for transitions in a given direction (inbound or outbound).
 * Shows a section title, description, and a collapsible card containing transition source
 * selectors and field mapping tables for each connected node. For single-node transitions,
 * the source description is shown in the card's always-visible area; for merging transitions
 * each node gets its own section inside the card body.
 */
function TransitionSettingsContent(props: TransitionSettingsContentProps): JSX.Element {
  const {classes: cardClasses, cx: cardCx} = useTransitionCardStyles();
  const {
    direction,
    selectedNode,
    transitionGroups,
    handleTransitionSourceSelect,
    locked,
    selectedNodeIsOutputNode,
  } = props;

  const {title, emptyMessage, preposition} = DIRECTION_CONFIG[direction];

  if (transitionGroups.length === 0) {
    return (
      <div>
        <div className={css(styles.sectionTitle)}>{title}</div>
        <div className={css(styles.emptyDirectionMessage)}>{emptyMessage}</div>
      </div>
    );
  }

  return (
    <div>
      <div className={css(styles.sectionTitle)}>{title}</div>
      <div className={css(styles.description)}>
        <span>Configure behavior and mappings for transitions {preposition}&nbsp;</span>
        <Chip
          className={cardCx(cardClasses.chipNormalWeight, cardClasses.displayOnlyChip)}
          text={selectedNode.data.nodeName}
          icon={selectedNode.data.nodeIcon}
          size={ChipSize.MINI}
        />
      </div>
      {transitionGroups.map((group) => (
        <TransitionGroupContent
          key={`${group.transition.id}-${group.nodes.map((node) => node.id).join('+')}`}
          direction={direction}
          transition={group.transition}
          connectedNodes={group.nodes}
          selectedNode={selectedNode}
          selectedNodeIsOutputNode={selectedNodeIsOutputNode}
          handleTransitionSourceSelect={handleTransitionSourceSelect}
          locked={locked}
        />
      ))}
    </div>
  );
}

function getTaskSchemaNodePTDVId(node: TaskSchemaNode): string {
  return node.data.taskDefinitionVersionSpec.clientId;
}

/**
 * Get the transition source for a node.  Check the transition first, and then fall back to a
 * default when the transition does not have an entry for that PTDV
 */
function getTransitionSourceForNode(transition: Transition, node: TaskSchemaNode): TransitionSource {
  const explicitSource = transition.transitionSourcesBySourcePTDVId[getTaskSchemaNodePTDVId(node)];
  if (explicitSource != null) {
    return explicitSource;
  }
  return getDefaultTransitionSourceForNode({id: node.id, sections: node.data.sections});
}

function findInteractiveTaskNodeById(
  nodes: Array<FlowchartNode>,
  nodeId: string
): TaskSchemaNode | undefined {
  const node = nodes.find((candidate) => candidate.id === nodeId);
  return node != null && isInteractiveTaskNode(node) ? node : undefined;
}

/**
 * Groups source nodes by the fulfilled transition they belong to, for rendering one card
 * per transition. A transition is "fulfilled" at the target when every one of its source
 * PTDVs is present among `sourceNodes` (i.e. reaches the target, incl. via routers).
 *
 * Transitions whose source set is a proper subset of another fulfilled transition to the
 * same target are omitted. Nodes sharing a PTDV within a group are deduplicated (one
 * representative per PTDV, earliest by `getNodeName`). Unfulfilled transitions are omitted.
 */
function groupNodesByTransition({
  sourceNodes,
  targetPTDVId,
  transitionsById,
}: {
  sourceNodes: Array<TaskSchemaNode>;
  targetPTDVId: string;
  transitionsById: Record<string, Transition>;
}): Array<{transition: Transition; nodes: Array<TaskSchemaNode>}> {
  const upstreamPTDVs = new Set(sourceNodes.map((node) => getTaskSchemaNodePTDVId(node)));

  const fulfilledTransitions = Object.values(transitionsById).filter(
    (transition) =>
      transition.targetPTDVId === targetPTDVId &&
      transition.sourcePTDVIds.every((sourcePTDVId) => upstreamPTDVs.has(sourcePTDVId))
  );

  const nonSubsetTransitions = fulfilledTransitions.filter(
    (transition) =>
      !fulfilledTransitions.some(
        (otherTransition) =>
          otherTransition.id !== transition.id &&
          transition.sourcePTDVIds.every((sourcePTDVId) =>
            otherTransition.sourcePTDVIds.includes(sourcePTDVId)
          )
      )
  );

  return nonSubsetTransitions.map((transition) => {
    const nodes = sourceNodes.filter((node) =>
      transition.sourcePTDVIds.includes(getTaskSchemaNodePTDVId(node))
    );
    return {transition, nodes};
  });
}

function areAllNodesInteractiveTaskNodes(nodes: Array<FlowchartNode>): nodes is Array<TaskSchemaNode> {
  return nodes.every((node) => isInteractiveTaskNode(node));
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element | null {
  const {selectedNode, interactiveFlowchart, onChangeTransitionSource, flowchartEditorConfigurator} =
    NodeDetailsSidepanelContext.useContext();
  const {transitionsById} = useTransitionsContext();
  assert(
    isInteractiveTaskNode(selectedNode),
    'Expected selected node to be a task node when calculating inbound transition groups'
  );

  const {upstreamMappableNodes, inboundTransitionGroups} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);

    const upstreamMappableNodes = interactiveFlowchart.nodes.filter((node) =>
      upstreamMappableNodeIds.includes(node.id)
    );
    assert(
      areAllNodesInteractiveTaskNodes(upstreamMappableNodes),
      'Expected all upstream mappable nodes to be task nodes'
    );

    const inboundTransitionGroups = groupNodesByTransition({
      sourceNodes: upstreamMappableNodes,
      targetPTDVId: selectedNode.data.taskDefinitionVersionSpec.clientId,
      transitionsById,
    });

    return {upstreamMappableNodes, inboundTransitionGroups};
  }, [interactiveFlowchart, selectedNode, transitionsById]);

  const {downstreamMappableNodes, outboundTransitionGroups} = useMemo(() => {
    const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const downstreamMappableNodes = interactiveFlowchart.nodes.filter((node) =>
      downstreamMappableNodeIds.includes(node.id)
    );
    assert(
      areAllNodesInteractiveTaskNodes(downstreamMappableNodes),
      'Expected all downstream mappable nodes to be task nodes'
    );
    const outboundTransitionGroups: Record<string, {transition: Transition; nodes: Array<TaskSchemaNode>}> =
      {};
    const selectedPTDVId = selectedNode.data.taskDefinitionVersionSpec.clientId;
    const flowchartNodes = interactiveFlowchart.nodes;

    for (const targetNode of downstreamMappableNodes) {
      // Find all fulfilled transitions on the target node that include the selected node as a
      // source
      const upstreamMappableNodeIdsForTarget = findAllUpstreamMappableNodeIds(
        interactiveFlowchart,
        targetNode.id
      );
      const inboundSourceNodes = flowchartNodes.filter((node) =>
        upstreamMappableNodeIdsForTarget.includes(node.id)
      );
      assert(
        areAllNodesInteractiveTaskNodes(inboundSourceNodes),
        'Expected all upstream source nodes for outbound grouping to be task nodes'
      );

      const targetInboundGroups = groupNodesByTransition({
        sourceNodes: inboundSourceNodes,
        targetPTDVId: targetNode.data.taskDefinitionVersionSpec.clientId,
        transitionsById,
      });

      for (const group of targetInboundGroups) {
        if (group.transition.sourcePTDVIds.includes(selectedPTDVId)) {
          if (outboundTransitionGroups[group.transition.id] != null) {
            outboundTransitionGroups[group.transition.id].nodes.push(targetNode);
          } else {
            outboundTransitionGroups[group.transition.id] = {
              transition: group.transition,
              nodes: [targetNode],
            };
          }
        }
      }
    }

    return {downstreamMappableNodes, outboundTransitionGroups: Object.values(outboundTransitionGroups)};
  }, [interactiveFlowchart, selectedNode, transitionsById]);

  const {nodes} = interactiveFlowchart;
  const outboundConnectedNodes = downstreamMappableNodes;

  const selectedNodeIsOutputNode =
    selectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

  const handleTransitionSourceSelect = (
    nodeIdToUpdate: string,
    value: TransitionSource,
    transition: Transition
  ): void => {
    const isInbound = upstreamMappableNodes.some((node) => node.id === nodeIdToUpdate);

    const transitionSourceNode = isInbound
      ? findInteractiveTaskNodeById(nodes, nodeIdToUpdate)
      : selectedNode;
    if (transitionSourceNode == null) {
      return;
    }

    const currentTransitionSource = getTransitionSourceForNode(transition, transitionSourceNode);

    if (currentTransitionSource !== value) {
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.TRANSITION_SOURCE_CHANGED, {
        oldValue: currentTransitionSource,
        newValue: value,
        flowchartType: flowchartEditorConfigurator.flowchartTypeName,
        actionMethod: ActionMethod.BUTTON_CLICK,
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        nodeType: getExecutionNodeTypeIdentifierForNode(selectedNode),
      });
      onChangeTransitionSource(transition.id, getTaskSchemaNodePTDVId(transitionSourceNode), value);
    }
  };

  const hasOutbound = outboundConnectedNodes.length > 0;
  const showOutbound = hasOutbound || !selectedNodeIsOutputNode;

  return (
    <div key={selectedNode.id}>
      <TransitionSettingsContent
        direction={TransitionDirection.INBOUND}
        selectedNode={selectedNode}
        selectedNodeIsOutputNode={selectedNodeIsOutputNode}
        transitionGroups={inboundTransitionGroups}
        handleTransitionSourceSelect={handleTransitionSourceSelect}
        locked={props.locked}
      />
      {showOutbound && <div className={css(styles.sectionDivider)} />}
      {showOutbound && (
        <TransitionSettingsContent
          direction={TransitionDirection.OUTBOUND}
          selectedNode={selectedNode}
          selectedNodeIsOutputNode={selectedNodeIsOutputNode}
          transitionGroups={outboundTransitionGroups}
          handleTransitionSourceSelect={handleTransitionSourceSelect}
          locked={props.locked}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  sectionDivider: {
    height: 24,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: 8,
  },
  description: {
    fontSize: '14px',
    fontWeight: 400,
    color: Color.GRAY_900,
    flexWrap: 'nowrap',
    marginBottom: 12,
  },
  cardBody: {
    fontSize: 14,
    color: Color.GRAY_900,
    paddingLeft: 16,
    paddingBottom: 12,
  },
  mergingNodeBlock: {
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: 12,
  },
  mergingNodeBlockHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    paddingLeft: 16,
    paddingRight: 16,
  },
  emptyDirectionMessage: {
    fontSize: 14,
    color: Color.GRAY_600,
    marginTop: 4,
  },
  emptyMappingsText: {
    fontSize: 14,
    color: Color.GRAY_600,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 12,
  },
  warningMessage: {
    marginTop: '4px',
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  warningIcon: {
    paddingRight: 4,
    color: ORANGE_DARK,
  },
  fieldSourceDropdownWrapper: {
    display: 'inline-block',
  },
});
